import React from 'react';
import { useTranslation } from 'react-i18next';
import { TranslationKey } from '../../i18n/translationKeys';
import { useTasksStore } from '../../stores/tasksStore';
import { AiTextRenderer } from '../../components/AITextRenderer/AiTextRenderer';
import { AiSparkle } from '../../components/AiSparkle/AiSparkle';
import { HomeGreetingSkeleton } from './HomeGreetingSkeleton';
import type { HomeGreetingProps } from './types';

export const HomeGreeting: React.FC<HomeGreetingProps> = ({ aiGreeting, relanceAccessible }) => {
    const { t } = useTranslation();
    const activeTaskCount = useTasksStore(s => s.activeTaskCount);
    const loading = useTasksStore(s => s.loading);

    // The "you have N relance to handle" line is itself relance-derived information — never
    // show it once access is known to be denied, regardless of what activeTaskCount holds.
    // Once a count has loaded it stays through reloads and failed reloads, like the list below.
    const tasksLoaded = relanceAccessible && activeTaskCount !== null;

    if (!tasksLoaded && !aiGreeting) return relanceAccessible && loading ? <HomeGreetingSkeleton /> : null;

    return (
        <div aria-live="polite" aria-atomic="false" aria-relevant="additions text" className="flex flex-col gap-s">
            <p data-testid="home-greeting-heading" className="flex items-center gap-s text-body-emphasized font-semibold text-content">
                <AiSparkle testId="home-greeting-sparkle" />
                {t(TranslationKey.Home.Greeting.SummaryHeading)}
            </p>
            <p className="text-m font-normal text-content">
                {tasksLoaded && (
                    <span data-testid="home-greeting-part2">
                        {t(TranslationKey.Home.Greeting.RelanceCount, { count: activeTaskCount ?? 0 })}
                    </span>
                )}
                {tasksLoaded && aiGreeting && ' '}
                {aiGreeting && (
                    <span data-testid="home-greeting-part3">
                        <AiTextRenderer text={aiGreeting} />
                    </span>
                )}
            </p>
        </div>
    );
};
